import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { PublicRoute } from "@/components/layout/PublicRoute";
import { ArtistRoute } from "@/components/layout/ArtistRoute";
import { PageSkeleton } from "@/components/ui/LoadingSpinner";
import Login from "@/pages/Login";

// Lazy-loaded pages
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AgendaGaleria = lazy(() => import("@/pages/agenda/AgendaGaleria"));
const AgendaWeek = lazy(() => import("@/pages/agenda/AgendaWeek"));
const AgendaList = lazy(() => import("@/pages/agenda/AgendaList"));
const AgendaDateTimeline = lazy(() => import("@/pages/agenda/AgendaDateTimeline"));
const AgendaLanes = lazy(() => import("@/pages/agenda/AgendaLanes"));
const OperationalPanel = lazy(() => import("@/pages/operations/OperationalPanel"));
const ProgramsList = lazy(() => import("@/pages/programs/ProgramsList"));
const ProgramEventDetail = lazy(() => import("@/pages/programs/ProgramEventDetail"));
const ArtistsList = lazy(() => import("@/pages/artists/ArtistsList"));
const ArtistDetail = lazy(() => import("@/pages/artists/ArtistDetail"));
const ArtworksList = lazy(() => import("@/pages/artworks/ArtworksList"));
const ArtworkDetail = lazy(() => import("@/pages/artworks/ArtworkDetail"));
const CatalogsList = lazy(() => import("@/pages/catalogs/CatalogsList"));
const CatalogPreviewPage = lazy(() => import("@/pages/catalogs/CatalogPreviewPage"));
const PublicCatalogPage = lazy(() => import("@/pages/catalogs/PublicCatalogPage"));
const MediaAssetsList = lazy(() => import("@/pages/media/MediaAssetsList"));
const ItemsList = lazy(() => import("@/pages/items/ItemsList"));
const ItemDetail = lazy(() => import("@/pages/items/ItemDetail"));
const Categories = lazy(() => import("@/pages/Categories"));
const Locations = lazy(() => import("@/pages/Locations"));
const Stock = lazy(() => import("@/pages/inventory/Stock"));
const Movements = lazy(() => import("@/pages/inventory/Movements"));
const RecipientsList = lazy(() => import("@/pages/recipients/RecipientsList"));
const RecipientTimeline = lazy(() => import("@/pages/recipients/RecipientTimeline"));
const RecipientListsPage = lazy(() => import("@/pages/recipients/lists/RecipientListsPage"));
const RecipientListDetail = lazy(() => import("@/pages/recipients/lists/RecipientListDetail"));
const Conversations = lazy(() => import("@/pages/conversations/Conversations"));
const ShipmentsList = lazy(() => import("@/pages/shipments/ShipmentsList"));
const NewShipment = lazy(() => import("@/pages/shipments/NewShipment"));
const ShipmentDetail = lazy(() => import("@/pages/shipments/ShipmentDetail"));
const DeclaracaoConteudoPage = lazy(() => import("@/pages/shipments/DeclaracaoConteudoPage"));
const Kits = lazy(() => import("@/pages/Kits"));
const OfflineActions = lazy(() => import("@/pages/OfflineActions"));
const OfflineActionDetail = lazy(() => import("@/pages/offline-actions/OfflineActionDetail"));
const Audit = lazy(() => import("@/pages/Audit"));
const Users = lazy(() => import("@/pages/Users"));
const Imports = lazy(() => import("@/pages/Imports"));
const GalleryConfigPage = lazy(() => import("@/pages/GalleryConfigPage"));
const Relatorios = lazy(() => import("@/pages/Relatorios"));
const FinanceiroCadastros = lazy(() => import("@/pages/financeiro/CadastrosPage"));
const FinanceiroDashboard = lazy(() => import("@/pages/financeiro/DashboardPage"));
const FinanceiroReceber = lazy(() => import("@/pages/financeiro/ContasReceberPage"));
const FinanceiroPagar = lazy(() => import("@/pages/financeiro/ContasPagarPage"));
const FinanceiroRepasses = lazy(() => import("@/pages/financeiro/PagamentosArtistasPage"));
const FinanceiroDevSistema = lazy(() => import("@/pages/financeiro/DesenvolvimentoSistemaPage"));
const FinanceiroConciliacao = lazy(() => import("@/pages/financeiro/ConciliacaoBancariaPage"));
const CampanhasListPage = lazy(() => import("@/pages/comunicacao/CampanhasListPage"));
const CampanhaDetailPage = lazy(() => import("@/pages/comunicacao/CampanhaDetailPage"));
const ConteudosListPage = lazy(() => import("@/pages/comunicacao/ConteudosListPage"));
const ConteudoDetailPage = lazy(() => import("@/pages/comunicacao/ConteudoDetailPage"));
const AtividadesListPage = lazy(() => import("@/pages/comunicacao/AtividadesListPage"));
const AtividadeDetailPage = lazy(() => import("@/pages/comunicacao/AtividadeDetailPage"));
const KanbanPage = lazy(() => import("@/pages/comunicacao/KanbanPage"));
const CalendarioPage = lazy(() => import("@/pages/comunicacao/CalendarioPage"));
const CalendarioEditorialPage = lazy(() => import("@/pages/comunicacao/CalendarioEditorialPage"));
const ComunicacaoDashboardPage = lazy(() => import("@/pages/comunicacao/ComunicacaoDashboardPage"));
const AprovacoesPage = lazy(() => import("@/pages/comunicacao/AprovacoesPage"));
const BancoAssetsPage = lazy(() => import("@/pages/comunicacao/BancoAssetsPage"));
const SimulacaoInstagramPage = lazy(() => import("@/pages/comunicacao/SimulacaoInstagramPage"));
const MetricasPage = lazy(() => import("@/pages/comunicacao/MetricasPage"));
const ContatosImprensaPage = lazy(() => import("@/pages/comunicacao/ContatosImprensaPage"));
const PressReleasesPage = lazy(() => import("@/pages/comunicacao/PressReleasesPage"));
const PortalLoginPage = lazy(() => import("@/pages/portal/PortalLoginPage"));
const PortalExtratoPage = lazy(() => import("@/pages/portal/PortalExtratoPage"));
const PortalRepassePage = lazy(() => import("@/pages/portal/PortalRepassePage"));
const ProjectsList = lazy(() => import("@/pages/projects/ProjectsList"));
const ProjectDetail = lazy(() => import("@/pages/projects/ProjectDetail"));
const PipelinePage = lazy(() => import("@/pages/pipeline/PipelinePage"));
const LeadsPage = lazy(() => import("@/pages/leads/LeadsPage"));
const CampanhasEmailPage = lazy(() => import("@/pages/marketing/CampanhasEmailPage"));
const CampanhaEmailDetailPage = lazy(() => import("@/pages/marketing/CampanhaEmailDetailPage"));
const PedidosPage = lazy(() => import("@/pages/pedidos/PedidosPage"));
const PedidoDetailPage = lazy(() => import("@/pages/pedidos/PedidoDetailPage"));
const PedidoFormPage = lazy(() => import("@/pages/pedidos/PedidoFormPage"));
const CatalogPrintPage = lazy(() => import("@/pages/catalogs/CatalogPrintPage"));
const EventObrasPrintPage = lazy(() => import("@/pages/programs/EventObrasPrintPage"));
const CatalogsInPipelinePage = lazy(() => import("@/pages/catalogs/CatalogsInPipelinePage"));
const CatalogTrackingPage = lazy(() => import("@/pages/catalogs/CatalogTrackingPage"));
const AiAssistants = lazy(() => import("@/pages/AiAssistants"));
const GerencialDashboard = lazy(() => import("@/pages/GerencialDashboard"));
const PedidoPublicPage = lazy(() => import("@/pages/public/PedidoPublicPage"));
const PublicArtworkListPage = lazy(() => import("@/pages/public/PublicArtworkListPage"));
const PublicRecipientListPage = lazy(() => import("@/pages/publica/PublicListView"));
const ArtworkListsPage = lazy(() => import("@/pages/artwork_lists/ArtworkListsPage"));
const ArtworkListDetail = lazy(() => import("@/pages/artwork_lists/ArtworkListDetail"));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

export default function App() {
  return (
    <Routes>
      {/* Public — redirects to /dashboard if already logged in */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/catalog-public/:slug" element={<LazyPage><PublicCatalogPage /></LazyPage>} />
      <Route path="/pedido/:token" element={<LazyPage><PedidoPublicPage /></LazyPage>} />
      <Route path="/listas/public/:token" element={<LazyPage><PublicArtworkListPage /></LazyPage>} />
      <Route path="/publica/lista/:token" element={<LazyPage><PublicRecipientListPage /></LazyPage>} />

      {/* Print page — protected but outside Layout (no sidebar / navbar) */}
      <Route
        path="/catalogs/:id/imprimir"
        element={
          <ProtectedRoute>
            <LazyPage><CatalogPrintPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/programs/:id/obras/imprimir"
        element={
          <ProtectedRoute>
            <LazyPage><EventObrasPrintPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipments/:id/declaracao"
        element={
          <ProtectedRoute>
            <LazyPage><DeclaracaoConteudoPage /></LazyPage>
          </ProtectedRoute>
        }
      />

      {/* Portal do Artista — audiência separada (sem sidebar interno) */}
      <Route path="/portal/login" element={<LazyPage><PortalLoginPage /></LazyPage>} />
      <Route
        path="/portal"
        element={
          <ArtistRoute>
            <LazyPage><PortalExtratoPage /></LazyPage>
          </ArtistRoute>
        }
      />
      <Route
        path="/portal/repasses/:id"
        element={
          <ArtistRoute>
            <LazyPage><PortalRepassePage /></LazyPage>
          </ArtistRoute>
        }
      />

      {/* Protected layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<LazyPage><Dashboard /></LazyPage>} />
        <Route path="gerencial" element={<LazyPage><GerencialDashboard /></LazyPage>} />
        <Route path="agenda" element={<LazyPage><AgendaGaleria /></LazyPage>} />
        <Route path="agenda/week" element={<LazyPage><AgendaWeek /></LazyPage>} />
        <Route path="agenda/list" element={<LazyPage><AgendaList /></LazyPage>} />
        <Route path="agenda/data/:date" element={<LazyPage><AgendaDateTimeline /></LazyPage>} />
        <Route path="agenda/lanes" element={<LazyPage><AgendaLanes /></LazyPage>} />
        <Route path="operations" element={<LazyPage><OperationalPanel /></LazyPage>} />
        <Route path="projects" element={<LazyPage><ProjectsList /></LazyPage>} />
        <Route path="projects/:id" element={<LazyPage><ProjectDetail /></LazyPage>} />
        <Route path="pipeline" element={<LazyPage><PipelinePage /></LazyPage>} />
        <Route path="leads" element={<LazyPage><LeadsPage /></LazyPage>} />
        <Route path="marketing/campanhas-email" element={<LazyPage><CampanhasEmailPage /></LazyPage>} />
        <Route path="marketing/campanhas-email/:mailchimpId" element={<LazyPage><CampanhaEmailDetailPage /></LazyPage>} />
        <Route path="pedidos" element={<LazyPage><PedidosPage /></LazyPage>} />
        <Route path="pedidos/novo" element={<LazyPage><PedidoFormPage /></LazyPage>} />
        <Route path="pedidos/:id/editar" element={<LazyPage><PedidoFormPage /></LazyPage>} />
        <Route path="pedidos/:id" element={<LazyPage><PedidoDetailPage /></LazyPage>} />
        <Route path="programs" element={<LazyPage><ProgramsList /></LazyPage>} />
        <Route path="programs/:id" element={<LazyPage><ProgramEventDetail /></LazyPage>} />
        <Route path="artists" element={<LazyPage><ArtistsList /></LazyPage>} />
        <Route path="artists/:slug" element={<LazyPage><ArtistDetail /></LazyPage>} />
        <Route path="artworks" element={<LazyPage><ArtworksList /></LazyPage>} />
        <Route path="artworks/:id" element={<LazyPage><ArtworkDetail /></LazyPage>} />
        <Route path="listas" element={<LazyPage><ArtworkListsPage /></LazyPage>} />
        <Route path="listas/:id" element={<LazyPage><ArtworkListDetail /></LazyPage>} />
        <Route path="catalogs" element={<LazyPage><CatalogsList /></LazyPage>} />
        <Route path="catalogs/:id/preview" element={<LazyPage><CatalogPreviewPage /></LazyPage>} />
        <Route path="catalogs-pipeline" element={<LazyPage><CatalogsInPipelinePage /></LazyPage>} />
        <Route path="catalogs-tracking" element={<LazyPage><CatalogTrackingPage /></LazyPage>} />
        <Route path="media-assets" element={<LazyPage><MediaAssetsList /></LazyPage>} />
        <Route path="items" element={<LazyPage><ItemsList /></LazyPage>} />
        <Route path="items/:id" element={<LazyPage><ItemDetail /></LazyPage>} />
        <Route path="categories" element={<LazyPage><Categories /></LazyPage>} />
        <Route path="locations" element={<LazyPage><Locations /></LazyPage>} />
        <Route path="stock" element={<LazyPage><Stock /></LazyPage>} />
        <Route path="movements" element={<LazyPage><Movements /></LazyPage>} />
        {/* Recipients: 2-column layout at /recipients and /recipients/:id */}
        <Route path="recipients" element={<LazyPage><RecipientsList /></LazyPage>} />
        <Route path="recipients/:id" element={<LazyPage><RecipientsList /></LazyPage>} />
        <Route path="recipients/:id/timeline" element={<LazyPage><RecipientTimeline /></LazyPage>} />
        {/* Listas de Destinatários */}
        <Route path="destinatarios/listas" element={<LazyPage><RecipientListsPage /></LazyPage>} />
        <Route path="destinatarios/listas/:id" element={<LazyPage><RecipientListDetail /></LazyPage>} />
        <Route path="conversations" element={<LazyPage><Conversations /></LazyPage>} />
        <Route path="shipments" element={<LazyPage><ShipmentsList /></LazyPage>} />
        <Route path="shipments/new" element={<LazyPage><NewShipment /></LazyPage>} />
        <Route path="shipments/:id" element={<LazyPage><ShipmentDetail /></LazyPage>} />
        <Route path="kits" element={<LazyPage><Kits /></LazyPage>} />
        <Route path="offline-actions" element={<LazyPage><OfflineActions /></LazyPage>} />
        <Route path="offline-actions/:id" element={<LazyPage><OfflineActionDetail /></LazyPage>} />
        <Route
          path="relatorios"
          element={
            <ProtectedRoute requiredPermissions={["financeiro", "diretoria"]}>
              <LazyPage><Relatorios /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><ComunicacaoDashboardPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/aprovacoes"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><AprovacoesPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/assets"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><BancoAssetsPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/simulacao"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><SimulacaoInstagramPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/metricas"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><MetricasPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/campanhas"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><CampanhasListPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/campanhas/:id"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><CampanhaDetailPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/conteudos"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><ConteudosListPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/conteudos/:id"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><ConteudoDetailPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/atividades"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><AtividadesListPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/atividades/:id"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><AtividadeDetailPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/kanban"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><KanbanPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/calendario"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><CalendarioPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="comunicacao/calendario-editorial"
          element={
            <ProtectedRoute requiredPermissions={["comunicacao"]}>
              <LazyPage><CalendarioEditorialPage /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route path="comunicacao/contatos-imprensa" element={
          <ProtectedRoute requiredPermissions={["comunicacao"]}>
            <LazyPage><ContatosImprensaPage /></LazyPage>
          </ProtectedRoute>
        } />
        <Route path="comunicacao/press-releases" element={
          <ProtectedRoute requiredPermissions={["comunicacao"]}>
            <LazyPage><PressReleasesPage /></LazyPage>
          </ProtectedRoute>
        } />
        <Route
          path="financeiro"
          element={
            <ProtectedRoute requiredPermissions={["financeiro", "diretoria"]}>
              <LazyPage><FinanceiroDashboard /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="financeiro/contas-receber"
          element={
            <ProtectedRoute requiredPermissions={["financeiro", "diretoria"]}>
              <LazyPage><FinanceiroReceber /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="financeiro/contas-pagar"
          element={
            <ProtectedRoute requiredPermissions={["financeiro", "diretoria"]}>
              <LazyPage><FinanceiroPagar /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="financeiro/pagamentos-artistas"
          element={
            <ProtectedRoute requiredPermissions={["financeiro", "diretoria"]}>
              <LazyPage><FinanceiroRepasses /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="financeiro/conciliacao"
          element={
            <ProtectedRoute requiredPermissions={["financeiro", "diretoria"]}>
              <LazyPage><FinanceiroConciliacao /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="financeiro/cadastros"
          element={
            <ProtectedRoute requiredPermissions={["financeiro", "diretoria"]}>
              <LazyPage><FinanceiroCadastros /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="financeiro/desenvolvimento"
          element={
            <ProtectedRoute requiredPermissions={["financeiro", "diretoria"]}>
              <LazyPage><FinanceiroDevSistema /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="audit"
          element={
            <ProtectedRoute requiredRoles={["admin"]}>
              <LazyPage><Audit /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute requiredRoles={["admin"]}>
              <LazyPage><Users /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="imports"
          element={
            <ProtectedRoute requiredRoles={["admin"]}>
              <LazyPage><Imports /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="ai-assistants"
          element={
            <ProtectedRoute requiredRoles={["admin"]}>
              <LazyPage><AiAssistants /></LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="gallery-config"
          element={
            <ProtectedRoute requiredRoles={["admin"]}>
              <LazyPage><GalleryConfigPage /></LazyPage>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
